import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { 
  Plus, Search, Clock, GripVertical, X, 
  ListMusic, RotateCcw, Timer, Play
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import RehearsalMode from "./RehearsalMode";

// Status badge colors
const STATUS_COLORS = {
  working: "bg-blue-500/20 text-blue-400",
  "stage-ready": "bg-green-500/20 text-green-400",
  retired: "bg-muted text-muted-foreground"
};

// Sortable Bit Card in Setlist
const SortableSetlistBit = ({ bit, timing, onRemove, onViewBit, onResetTiming, onEditBit }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: bit.bit_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    const fullBit = bit.bit_data || bit;
    onEditBit?.(fullBit);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 p-3 bg-card border border-border rounded-sm group"
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-1 text-muted-foreground hover:text-foreground"
      >
        <GripVertical className="w-4 h-4" />
      </button>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p 
            className="font-medium text-sm truncate cursor-pointer hover:text-primary"
            onClick={() => onViewBit(bit)}
          >
            {bit.bit_data?.title || bit.title || "Untitled"}
          </p>
          <button
            onClick={handleEditClick}
            className="text-xs text-primary hover:underline opacity-0 group-hover:opacity-100 transition-opacity"
            title="Edit this bit"
          >
            Edit
          </button>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Badge className={`text-xs ${STATUS_COLORS[bit.bit_data?.status] || STATUS_COLORS.working}`}>
            {bit.bit_data?.status || "working"}
          </Badge>
          {timing && (
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {formatDuration(timing.duration_seconds)}
              {timing.is_manual_override && (
                <button
                  onClick={(e) => { e.stopPropagation(); onResetTiming(bit.bit_id); }}
                  className="ml-1 text-primary hover:text-primary/80"
                  title="Reset to calculated time"
                >
                  <RotateCcw className="w-3 h-3" />
                </button>
              )}
            </span>
          )}
        </div>
      </div>
      
      <button
        onClick={() => onRemove(bit.bit_id)}
        className="p-1 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

// Available Bit Card
const AvailableBitCard = ({ bit, onAdd, onView, isInSetlist, onEditBit }) => {
  const getPreviewText = (bit) => {
    if (bit.sections?.length > 0) {
      const includedSections = bit.sections.filter(s => s.included !== false);
      const firstSection = includedSections[0];
      return firstSection?.content?.slice(0, 100) || "";
    }
    return bit.content?.slice(0, 100) || "";
  };

  return (
    <div 
      className={`p-3 bg-card border border-border rounded-sm hover:border-primary/50 transition-colors group ${isInSetlist ? 'opacity-50' : ''}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Badge className={`text-xs ${STATUS_COLORS[bit.status] || STATUS_COLORS.working}`}>
              {bit.status}
            </Badge>
            <span 
              className="font-medium text-sm truncate cursor-pointer hover:text-primary"
              onClick={() => onView(bit)}
            >
              {bit.title || "Untitled"}
            </span>
            <button
              onClick={() => onEditBit?.(bit)}
              className="text-xs text-primary hover:underline opacity-0 group-hover:opacity-100 transition-opacity"
              title="Edit this bit"
            >
              Edit
            </button>
          </div>
          <p 
            className="text-xs text-muted-foreground line-clamp-2 font-mono cursor-pointer"
            onClick={() => onView(bit)}
          >
            {getPreviewText(bit)}
          </p>
        </div>
        <Button
          size="sm"
          variant={isInSetlist ? "ghost" : "outline"}
          onClick={() => !isInSetlist && onAdd(bit.bit_id)}
          disabled={isInSetlist}
          className="shrink-0"
        >
          {isInSetlist ? "Added" : <Plus className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
};

// Bit Preview Dialog
const BitPreviewDialog = ({ bit, isOpen, onClose, onAddToSetlist, isInSetlist, onEditBit }) => {
  if (!bit) return null;

  const getFullContent = () => {
    if (bit.sections?.length > 0) {
      return bit.sections
        .filter(s => s.included !== false)
        .sort((a, b) => a.order - b.order)
        .map(s => s.content)
        .join("\n\n---\n\n");
    }
    return bit.content || "";
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {bit.title || "Untitled"}
            <Badge className={`text-xs ${STATUS_COLORS[bit.status] || STATUS_COLORS.working}`}>
              {bit.status}
            </Badge>
          </DialogTitle>
        </DialogHeader>
        <div 
          className="font-mono text-sm whitespace-pre-wrap leading-relaxed overflow-y-auto"
          style={{ maxHeight: '60vh' }}
        >
          {getFullContent()}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button 
            variant="secondary" 
            onClick={() => { onEditBit?.(bit); onClose(); }}
          >
            Edit Bit
          </Button>
          {!isInSetlist && (
            <Button onClick={() => { onAddToSetlist(bit.bit_id); onClose(); }}>
              <Plus className="w-4 h-4 mr-2" />
              Add to Setlist
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Setlist Planner Component (Main Content Area Only)
export default function SetlistPlanner({ 
  bits, 
  setlists, 
  setSetlists,
  selectedSetlistId, 
  setSelectedSetlistId,
  statusFilter,
  onEditBit
}) {
  const [selectedSetlist, setSelectedSetlist] = useState(null);
  const [setlistTiming, setSetlistTiming] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [previewBit, setPreviewBit] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isRehearsalMode, setIsRehearsalMode] = useState(false);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch selected setlist details when selection changes
  useEffect(() => {
    let isMounted = true;
    
    const loadSetlist = async () => {
      if (!selectedSetlistId) {
        if (isMounted) {
          setSelectedSetlist(null);
          setSetlistTiming(null);
        }
        return;
      }

      try {
        const [detailsRes, timingRes] = await Promise.all([
          fetch(`${API}/setlists/${selectedSetlistId}`, { credentials: 'include' }),
          fetch(`${API}/setlists/${selectedSetlistId}/timing`, { credentials: 'include' })
        ]);
        
        if (!isMounted) return;
        
        if (detailsRes.ok) {
          setSelectedSetlist(await detailsRes.json());
        }
        if (timingRes.ok) {
          setSetlistTiming(await timingRes.json());
        }
      } catch (error) {
        console.error("Failed to fetch setlist details:", error);
      }
    };
    
    loadSetlist();
    
    return () => { isMounted = false; };
  }, [selectedSetlistId]);

  const refreshSetlistDetails = async () => {
    if (!selectedSetlistId) return;
    try {
      const [detailsRes, timingRes] = await Promise.all([
        fetch(`${API}/setlists/${selectedSetlistId}`, { credentials: 'include' }),
        fetch(`${API}/setlists/${selectedSetlistId}/timing`, { credentials: 'include' })
      ]);
      
      if (detailsRes.ok) {
        setSelectedSetlist(await detailsRes.json());
      }
      if (timingRes.ok) {
        setSetlistTiming(await timingRes.json());
      }
    } catch (error) {
      console.error("Failed to fetch setlist details:", error);
    }
  };

  const handleAddBitToSetlist = async (bitId) => {
    if (!selectedSetlistId) {
      toast.error("Select a setlist first");
      return;
    }
    
    try {
      const response = await fetch(`${API}/setlists/${selectedSetlistId}/bits/${bitId}`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        await refreshSetlistDetails();
        toast.success("Bit added to setlist");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to add bit");
      }
    } catch (error) {
      toast.error("Failed to add bit");
    }
  };

  const handleRemoveBitFromSetlist = async (bitId) => {
    if (!selectedSetlistId) return;
    
    try {
      const response = await fetch(`${API}/setlists/${selectedSetlistId}/bits/${bitId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        await refreshSetlistDetails();
        toast.success("Bit removed");
      }
    } catch (error) {
      toast.error("Failed to remove bit");
    }
  };

  const handleResetTiming = async (bitId) => {
    if (!selectedSetlistId) return;
    
    try {
      const response = await fetch(`${API}/setlists/${selectedSetlistId}/bits/${bitId}/timing`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ manual_duration_seconds: null })
      });
      
      if (response.ok) {
        await refreshSetlistDetails();
        toast.success("Timing reset to estimate");
      }
    } catch (error) {
      toast.error("Failed to reset timing");
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id || !selectedSetlist) return;
    
    const oldIndex = selectedSetlist.bits.findIndex(b => b.bit_id === active.id);
    const newIndex = selectedSetlist.bits.findIndex(b => b.bit_id === over.id);
    
    if (oldIndex === -1 || newIndex === -1) return;
    
    // Optimistically update UI
    const newBits = arrayMove(selectedSetlist.bits, oldIndex, newIndex).map((b, i) => ({
      ...b,
      order: i
    }));
    
    setSelectedSetlist({ ...selectedSetlist, bits: newBits });
    
    // Update on server
    try {
      await fetch(`${API}/setlists/${selectedSetlistId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ bits: newBits.map(b => ({ bit_id: b.bit_id, order: b.order, manual_duration_seconds: b.manual_duration_seconds })) })
      });
      
      // Refresh timing
      const timingRes = await fetch(`${API}/setlists/${selectedSetlistId}/timing`, { credentials: 'include' });
      if (timingRes.ok) {
        setSetlistTiming(await timingRes.json());
      }
    } catch (error) {
      // Revert on error
      await refreshSetlistDetails();
      toast.error("Failed to reorder");
    }
  };

  const handleViewBit = (bit) => {
    // Get full bit data
    const fullBit = bit.bit_data || bits.find(b => b.bit_id === bit.bit_id) || bit;
    setPreviewBit(fullBit);
    setIsPreviewOpen(true);
  };

  // Filter bits
  const filteredBits = useMemo(() => {
    return bits.filter(bit => {
      const matchesSearch = !searchQuery || 
        bit.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        bit.content?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = !statusFilter || bit.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [bits, searchQuery, statusFilter]);

  // Get bit IDs in current setlist
  const setlistBitIds = useMemo(() => {
    return new Set(selectedSetlist?.bits?.map(b => b.bit_id) || []);
  }, [selectedSetlist]);

  // Get timing map for quick lookup
  const timingMap = useMemo(() => {
    const map = {};
    setlistTiming?.bits?.forEach(t => {
      map[t.bit_id] = t;
    });
    return map;
  }, [setlistTiming]);

  // No setlist selected state
  if (!selectedSetlistId) {
    return (
      <div className="flex-1 flex flex-col items-start justify-start overflow-auto">
        <div className="p-8">
          <ListMusic className="w-16 h-16 text-muted-foreground mb-4" />
          <h3 className="font-display text-xl uppercase mb-2">Setlist Planner</h3>
          <p className="text-muted-foreground text-sm mb-4">
            {setlists.length > 0 
              ? "Select a setlist from the sidebar to start planning"
              : "Create your first setlist to start planning your sets"
            }
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col items-stretch justify-start overflow-auto">
      {/* Setlist Header - More prominent */}
      <div className="p-4 border-b border-border flex-shrink-0 bg-primary/5">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <ListMusic className="w-5 h-5 text-primary" />
              <h2 className="font-display text-xl uppercase tracking-wider text-primary">{selectedSetlist?.name || "Loading..."}</h2>
            </div>
            <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Timer className="w-4 h-4" />
                {setlistTiming?.total_duration_formatted || "0:00"}
              </span>
              <span>{selectedSetlist?.bits?.length || 0} bits</span>
              {setlistTiming?.wpm && (
                <span className="text-xs">({setlistTiming.wpm} WPM)</span>
              )}
            </div>
          </div>
          <Button 
            size="sm" 
            className="gap-2 bg-primary text-primary-foreground"
            onClick={() => setIsRehearsalMode(true)}
            disabled={!selectedSetlist?.bits?.length}
          >
            <Play className="w-4 h-4" />
            Rehearse
          </Button>
        </div>
      </div>

      {/* Setlist Bits (Top) */}
      <div className="p-4 border-b border-border bg-muted/30 flex-shrink-0">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
          Set Order
        </h3>
        {selectedSetlist?.bits?.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={selectedSetlist.bits.map(b => b.bit_id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {selectedSetlist.bits
                  .sort((a, b) => a.order - b.order)
                  .map((bit) => (
                    <SortableSetlistBit
                      key={bit.bit_id}
                      bit={bit}
                      timing={timingMap[bit.bit_id]}
                      onRemove={handleRemoveBitFromSetlist}
                      onViewBit={handleViewBit}
                      onResetTiming={handleResetTiming}
                      onEditBit={onEditBit}
                    />
                  ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <ListMusic className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No bits in this setlist yet</p>
            <p className="text-xs">Add bits from below to build your set</p>
          </div>
        )}
      </div>

      {/* Available Bits (Bottom) */}
      <div className="flex-1 flex flex-col min-h-0 p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search bits..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 bg-muted border-0 text-sm"
            />
          </div>
          <span className="text-xs text-muted-foreground">
            {filteredBits.length} bits available
          </span>
        </div>
        
        <ScrollArea className="flex-1">
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3 pr-4">
            {filteredBits.map((bit) => (
              <AvailableBitCard
                key={bit.bit_id}
                bit={bit}
                onAdd={handleAddBitToSetlist}
                onView={handleViewBit}
                isInSetlist={setlistBitIds.has(bit.bit_id)}
                onEditBit={onEditBit}
              />
            ))}
          </div>
        </ScrollArea>
      </div>

      {/* Bit Preview Dialog */}
      <BitPreviewDialog
        bit={previewBit}
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        onAddToSetlist={handleAddBitToSetlist}
        isInSetlist={previewBit ? setlistBitIds.has(previewBit.bit_id) : false}
        onEditBit={onEditBit}
      />

      {/* Rehearsal Mode Overlay */}
      {isRehearsalMode && selectedSetlist && (
        <RehearsalMode
          setlistData={selectedSetlist}
          onClose={() => setIsRehearsalMode(false)}
          onRefreshSetlist={refreshSetlistDetails}
        />
      )}
    </div>
  );
}
